/**
 * @file RegionInfo/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 */

import { memo, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";

import VerticalCardList from "../VerticalCardList/component";
import RegionsListSelector from "./components/RegionsListSelector/component";
import Swimlane from "../Swimlane/component";

import { fetch_sectional_content_graphql } from "../../actions/SectionalContent/dispatchers";
import { PLACEMENT } from "./components/RegionsListSelector/constants";
import {
    VERTICAL_REGION_COUNT,
    SWIMLANE_REGION_COUNT,
    SWIMLANE_FEATURE,
    VERTICAL_FEATURE,
} from "./constants";

import { getLocalSection } from "../../actions/Regions/dispatchers";
import useLocalNewsRegion from "../../hooks/useLocalNewsRegion";
import featureAttr from "../../utils/featureAttr";
import contentGridStyles from "../ContentGrid/styles/themes/default.module.scss";

/**
 * Represents a Regional Swimlane. It can be as simple as just populating from a lineup
 * OR can filter by subjects/tags/etc by region.
 *
 * @param {string} section
 * @param {object} displayFilter - When a subject and/or category exclusion is used, it contains:
 *        @key {string} subjectsExpression - the subject expression used to query content
 *        @key {string} excludedCategorySet - a category to omit from the result set
 * @param {boolean} isSwimlane - Used to determine whether to display as a Swimlane or VerticalCardList
 */

const RegionInfo = ({
    section,
    subjects = "",
    excludedCategorySet = "",
    isSwimlane = true,
    type = "default",
    useGrid = false,
    cardClickHandler = () => {},
    pathSuffix = "",
}) => {
    const dispatch = useDispatch();
    const { region } = useLocalNewsRegion();
    const slug = region.categorySlug;
    const regionPath = region.path + pathSuffix;
    const sectionalContent = useSelector((state) => state.sectionalContent);
    const baseSection = useSelector((state) => state.app.baseSection);
    const path = useSelector((state) => state.app.path);
    const hasSubjects = subjects !== "" || excludedCategorySet !== "";

    useEffect(() => {
        if (!hasSubjects) {
            dispatch(
                fetch_sectional_content_graphql(
                    regionPath,
                    isSwimlane ? SWIMLANE_REGION_COUNT : VERTICAL_REGION_COUNT
                )
            );
        }
    }, [hasSubjects, regionPath, isSwimlane]);

    useEffect(() => {
        if (slug !== "" && (section || type === "flp")) {
            dispatch(
                getLocalSection(
                    path,
                    section,
                    slug,
                    subjects,
                    excludedCategorySet,
                    type
                )
            );
        }
    }, [slug, section, type, path, subjects, excludedCategorySet]);

    if (isSwimlane) {
        return (
            <Swimlane
                headline="My Local"
                section={section || regionPath}
                showMoreLink={false}
                customTitleBar={<RegionsListSelector />}
                hasHeadlineLink={false}
                className={`regionInfo ${contentGridStyles.regionInfo} sclt-regionalSwimlane`}
                itemCount={SWIMLANE_REGION_COUNT}
                fetchSection={false}
                key={`mylocal-${section || regionPath}`}
                featureAttribution={featureAttr(SWIMLANE_FEATURE)}
                useGrid={useGrid}
                cardClickHandler={cardClickHandler}
            />
        );
    } else {
        let contentList = null;

        if (sectionalContent) {
            const regionContent = sectionalContent[section || regionPath];

            if (regionContent && regionContent.sectionList) {
                contentList = regionContent.sectionList;
            }
        }

        return contentList ? (
            <VerticalCardList
                className="regionInfo sclt-regionalwidget"
                id={`regionInfo${baseSection}`}
                customTitleBar={
                    <RegionsListSelector placement={PLACEMENT.SIDEBAR} />
                }
                items={contentList}
                featureAttribution={featureAttr(VERTICAL_FEATURE)}
            />
        ) : null;
    }
};

RegionInfo.propTypes = {
    isSwimlane: PropTypes.bool,
};

export default memo(RegionInfo);
